<template>
  <ui-modal-layout :icon="SparklesIcon">
    <template #header>
      <h2 class="m-0 flex items-center gap-2 text-xl leading-tight font-semibold text-foreground">
        {{ tr('Infinite∞Creator') }}
        <ui-badge>Lite</ui-badge>
      </h2>
    </template>

    <p v-if="isLoading && entity === null" class="m-0 flex items-center justify-center gap-2 py-12 text-muted-foreground">
      <loader-circle-icon class="size-4 animate-spin" />
      {{ tr('Loading...') }}
    </p>

    <template v-else-if="entity !== null">
      <section class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <h3 class="m-0 truncate text-sm font-semibold text-foreground">{{ tr('Post text') }}</h3>
          <div v-if="isCompleted" class="flex shrink-0 gap-1.5">
            <ui-button
              variant="outline"
              size="xs"
              :disabled="isPublishing"
              @click="() => regenerateContent()"
            >
              <refresh-cw-icon />
              {{ tr('Regenerate text') }}
            </ui-button>
            <ui-button
              variant="outline"
              size="xs"
              :disabled="isPublishing"
              @click="() => openEditContentModal()"
            >
              <pencil-icon />
              {{ tr('Edit') }}
            </ui-button>
          </div>
        </div>
        <div v-if="isGeneratingTextContent || !entity.content" class="flex flex-col gap-1.5">
          <ui-skeleton class="h-4 w-full" />
          <ui-skeleton class="h-4 w-full" />
          <ui-skeleton class="h-4 w-4/5" />
        </div>
        <template v-else>
          <post-content-text :text="contentParts.text" />
          <p v-if="contentParts.disclosure" class="m-0 flex items-center gap-1.5 border-t border-border/60 pt-2 text-xs text-muted-foreground">
            <info-icon class="size-3.5 shrink-0" />
            {{ contentParts.disclosure }}
          </p>
        </template>
      </section>

      <div v-if="isInProgress && !isGeneratingTextContent" class="relative">
        <ui-skeleton class="aspect-square min-h-80 w-full rounded-xl" />
        <p class="absolute inset-0 m-0 flex items-center justify-center gap-2 text-muted-foreground">
          <loader-circle-icon class="size-4 animate-spin" />
          {{ tr('Content generation in progress...') }}
        </p>
      </div>
      <div v-else-if="entity.image?.url" class="flex flex-col gap-2.5">
        <div class="relative overflow-hidden rounded-xl">
          <magic-tool-generated-image-renderer
            ref="imageRenderer"
            class="[&_canvas]:w-full"
            :image-url="entity.image.url"
            :aspect-ratio="entity.visual_format"
            :logo-url="entity.logo ? entity.logo.url : null"
            :logo-size="entity.visual_logo_size"
            :text="entity.visual_text"
            :font-family="entity.visual_font_family"
            :font-weight="entity.visual_font_weight"
            :language="entity.language"
          />

          <div class="absolute inset-x-0 top-0 flex justify-end gap-1.5 bg-linear-to-b from-black/60 to-transparent p-2.5 pb-8">
            <ui-dropdown-menu>
              <ui-dropdown-menu-trigger as-child>
                <ui-button variant="secondary" size="xs" :disabled="isPublishing">
                  <refresh-cw-icon />
                  {{ tr('Regenerate image') }}
                </ui-button>
              </ui-dropdown-menu-trigger>
              <ui-dropdown-menu-content align="end">
                <ui-dropdown-menu-item @select="() => regenerateImage(false)">
                  {{ tr('Regenerate current image') }}
                </ui-dropdown-menu-item>
                <ui-dropdown-menu-item @select="() => regenerateImage(true)">
                  {{ tr('Regenerate with new concept') }}
                </ui-dropdown-menu-item>
              </ui-dropdown-menu-content>
            </ui-dropdown-menu>
            <ui-button
              variant="secondary"
              size="xs"
              :disabled="isPublishing"
              @click="() => openEditImageModal()"
            >
              <pencil-icon />
              {{ tr('Edit') }}
            </ui-button>
          </div>
        </div>

        <ui-button class="w-full" @click="() => $refs.imageRenderer.downloadImage()">
          <download-icon />
          {{ tr('Download image') }}
        </ui-button>
      </div>

      <section v-if="isCompleted" class="glass-tile flex flex-col gap-3 rounded-xl p-4">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Ready to Post?') }}</h3>
        <router-link
          v-if="brands.length === 0"
          to="/integrations/meta/pages"
          class="no-underline"
          @click="() => $fullscreenModal.closeAll()"
        >
          <info-text>
            {{ tr('To publish content, you need to connect at least one Facebook page or Instagram account.') }}
            {{ tr('You can do this under Integrations > Meta Business.') }}
          </info-text>
        </router-link>
        <template v-else>
          <brand-filter
            v-model="selectedBrandId"
            :all-option="false"
            :allow-inactive="false"
            :placeholder="tr('Select brand')"
          />

          <div
            v-if="availableTargetPlatforms.length !== 0"
            class="flex flex-wrap gap-2.5"
            role="group"
            :aria-label="tr('Platform')"
          >
            <label
              v-for="platform in availableTargetPlatforms"
              :key="'publish-target-platform--' + platform.value"
              :class="[
                'inline-flex h-9 cursor-pointer items-center gap-2.5 rounded-lg border px-3.5 text-sm text-foreground transition-colors',
                publishTargetPlatforms.includes(platform.value) ? 'border-primary bg-primary/10' : 'glass-input border-border',
              ]"
            >
              <ui-checkbox
                :model-value="publishTargetPlatforms.includes(platform.value)"
                class="size-4.5"
                @update:model-value="() => togglePlatform(platform.value)"
              />
              {{ platform.label }}
            </label>
          </div>

          <router-link
            v-if="showMissingPermissionError"
            to="/integrations/meta"
            class="no-underline"
            @click="() => $fullscreenModal.closeAll()"
          >
            <info-text type="error">
              {{ tr('Publishing is currently not allowed for the selected platforms.') }}
              {{ tr('To resolve this, please reconnect the Facebook profile that manages the integration.') }}
              {{ tr('You can do this under Integrations > Meta Business > Profiles.') }}
            </info-text>
          </router-link>
          <ui-button v-else :disabled="isPublishing" @click="() => publishContent()">
            <loader-circle-icon v-if="isPublishing" class="animate-spin" />
            <send-icon v-else />
            {{ tr('Publish content') }}
          </ui-button>
        </template>
      </section>

      <section v-if="publications.length !== 0" class="flex flex-col gap-2">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Publication log') }}</h3>
        <component
          :is="publicationRowTag(publication)"
          v-for="publication in publications"
          :key="'publication--' + publication.id"
          v-bind="publicationRowAttributes(publication)"
          :class="[ 'glass-tile flex items-center gap-3 rounded-xl p-3 text-foreground no-underline', publication.link ? 'cursor-pointer ' + GLOW_TRANSITION + ' hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]' : '' ]"
          @click="event => onPublicationClick(publication, event)"
        >
          <social-page
            class="min-w-0 flex-1"
            :source-profile="publication.source_profile"
            :date="publication.published_at"
            :date-suffix="publisherLabel(publication)"
          />
          <triangle-alert-icon
            v-if="publication.hasError"
            v-tooltip="publication.error_message || tr('An unexpected error occurred.')"
            class="size-5 shrink-0 text-destructive"
          />
          <chevron-right-icon v-else-if="publication.link" class="size-5 shrink-0 text-primary" />
        </component>
      </section>

      <section class="flex flex-col gap-3 border-t border-border pt-5">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Generation informations') }}</h3>
        <dl class="m-0 grid grid-cols-2 gap-x-4 gap-y-3 max-sm:grid-cols-1">
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Content goal') }}</dt>
            <dd class="m-0">{{ goalLabel }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Content language') }}</dt>
            <dd class="m-0">{{ languageLabel }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Content length') }}</dt>
            <dd class="m-0">{{ lengthLabel }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Industry') }}</dt>
            <dd class="m-0">{{ industryLabel }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Style, tone') }}</dt>
            <dd class="m-0">{{ toneLabel }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Image format') }}</dt>
            <dd class="m-0">{{ entity.visual_format }}</dd>
          </div>
          <div v-if="entity.website" class="flex min-w-0 flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Website') }}</dt>
            <dd class="m-0 truncate">
              <a :href="entity.website" target="_blank" class="text-primary hover:underline">{{ entity.website }}</a>
            </dd>
          </div>
          <div class="col-span-full flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Describe in your own words what the content should be about') }}</dt>
            <dd class="m-0 whitespace-pre-line">{{ entity.description }}</dd>
          </div>
        </dl>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Target audience') }}</h3>
        <dl class="m-0 grid grid-cols-2 gap-x-4 gap-y-3">
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Gender') }}</dt>
            <dd class="m-0">{{ genderLabel }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Age') }}</dt>
            <dd class="m-0">{{ entity.target_audience_age_min }} - {{ entity.target_audience_age_max }}</dd>
          </div>
        </dl>
      </section>
    </template>
  </ui-modal-layout>
</template>

<script>
import { ChevronRight, Download, Info, LoaderCircle, Pencil, RefreshCw, Send, Sparkles, TriangleAlert } from '@lucide/vue';
import { markRaw } from 'vue';

import { AI_STATUS, CONTENT_GENERATION_ITEM_CONTENT_LENGTHS, GENDERS } from '@/consts.js';
import CreatorLiteService from '@/services/CreatorLite.js';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import CreatorLiteContentEdit from '@/ui/components/CreatorLiteContentEdit.vue';
import CreatorLiteImageSettings from '@/ui/components/CreatorLiteImageSettings.vue';
import InfoText from '@/ui/components/InfoText.vue';
import MagicToolGeneratedImageRenderer from '@/ui/components/MagicToolGeneratedImageRenderer.vue';
import PostContentText from '@/ui/components/PostContentText.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { GLOW_TRANSITION } from '@/ui/utils/ruleAction.js';

export default {
  name: 'CreatorLiteItemDetails',
  components: {
    InfoIcon: Info,
    BrandFilter,
    ChevronRightIcon: ChevronRight,
    DownloadIcon: Download,
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    MagicToolGeneratedImageRenderer,
    PencilIcon: Pencil,
    PostContentText,
    RefreshCwIcon: RefreshCw,
    SendIcon: Send,
    SocialPage,
    TriangleAlertIcon: TriangleAlert,
  },
  data: () => ({
    GLOW_TRANSITION,
    SparklesIcon: markRaw(Sparkles),
    isLoading: false,
    isSaving: false,
    isPublishing: false,
    isGeneratingTextContent: false,
    entity: null,
    fetchTimer: null,
    selectedBrandId: null,
    publishTargetPlatforms: [],
  }),
  computed: {
    // The AI disclosure ("— — —" line + note) appended to the text is shown as a footnote.
    contentParts() {
      const match = this.entity.content.match(/\s*(?:— ?){3}\s*\n\s*ⓘ?\s*(.+?)\s*$/);
      if (!match) {
        return { text: this.entity.content, disclosure: null };
      }

      return { text: this.entity.content.slice(0, match.index), disclosure: match[1] };
    },
    id() {
      return this.$route.params.id;
    },
    languageLabel() {
      const languages = this.$store.getters['app/languagesISO'];
      const language = languages.find(lang => lang.iso_639_1 === this.entity?.language);

      return this.tr(language?.name || 'Unknown');
    },
    lengthLabel() {
      const length = CONTENT_GENERATION_ITEM_CONTENT_LENGTHS.find(l => l.value === this.entity?.content_length);

      return this.tr(length?.label || 'Unknown');
    },
    genderLabel() {
      const gender = GENDERS.find(g => g.value === this.entity?.target_audience_gender);

      return this.tr(gender?.label || 'Unknown');
    },
    goalLabel() {
      const goals = this.$store.getters['app/magicToolContentGenerationGoals'];
      const goal = goals.find(g => g.value === this.entity?.content_goal);

      return this.tr(goal?.label || 'Unknown');
    },
    industryLabel() {
      const industries = this.$store.getters['app/industries'];
      const industry = industries.find(i => i.value === this.entity?.industry);

      return this.tr(industry?.label || 'Unknown');
    },
    toneLabel() {
      const tones = this.$store.getters['app/tones'];
      const tone = tones.find(t => t.value === this.entity?.tone);

      return this.tr(tone?.label || 'Unknown');
    },
    isInProgress() {
      return this.entity?.isWaiting || this.entity?.isInProgress;
    },
    isCompleted() {
      return this.entity?.isCompleted;
    },
    brands() {
      return this.$root.currentCompany?.brands || [];
    },
    selectedBrand() {
      if (!this.selectedBrandId) {
        return null;
      }
      return this.brands.find(b => b.id === this.selectedBrandId) || null;
    },
    selectedBrandSourceProfiles() {
      return this.selectedBrand?.sourceProfiles || [];
    },
    availableTargetPlatforms() {
      return this.selectedBrandSourceProfiles.map(sp => ({
        value: sp.platform,
        label: sp.platform[0].toUpperCase() + sp.platform.slice(1),
      }));
    },
    publications() {
      return this.entity?.publications || [];
    },
    showMissingPermissionError() {
      if (this.selectedBrandSourceProfiles.length === 0) {
        return false;
      }

      return !!this.selectedBrandSourceProfiles.find(sp => !sp.has_post_permission);
    },
  },
  watch: {
    availableTargetPlatforms() {
      this.publishTargetPlatforms = this.availableTargetPlatforms.map(platform => platform.value);
    },
  },
  created() {
    this.loadData();

    const activeBrands = this.brands.filter(brand => !brand.isInactive);
    if (activeBrands.length === 1) {
      this.selectedBrandId = activeBrands[0].id;
    }
  },
  beforeUnmount() {
    this.destroyFetchTimer();
  },
  methods: {
    togglePlatform(platform) {
      this.publishTargetPlatforms = this.publishTargetPlatforms.includes(platform)
        ? this.publishTargetPlatforms.filter(item => item !== platform)
        : [ ...this.publishTargetPlatforms, platform ];
    },
    publisherLabel(publication) {
      if (!publication.user) {
        return null;
      }

      return this.tr('by :name', { ':name': publication.user.fullname });
    },
    publicationRowTag(publication) {
      return publication.link ? 'a' : 'div';
    },
    publicationRowAttributes(publication) {
      if (!publication.link) {
        return {};
      }

      if (!publication.post_id) {
        return { href: publication.link, target: '_blank', rel: 'noopener' };
      }

      return { href: publication.link };
    },
    onPublicationClick(publication, event) {
      // A modified click opens the post in a new tab: the browser does the
      // navigation, and this modal stays where it is.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }

      if (!publication.post_id) {
        return;
      }

      // Navigated here instead of by the link, so the post modal knows where to
      // return to when it is closed.
      event.preventDefault();
      this.$fullscreenModal.closeAll();
      this.$router.push(publication.link + '?back=' + encodeURIComponent('/creator-lite/' + this.id));
    },
    loadData() {
      this.isLoading = true;

      return CreatorLiteService.get(this.id)
        .then(resp => {
          this.entity = resp;

          if (this.isInProgress) {
            this.initFetchTimer();
          }
          else {
            this.isGeneratingTextContent = false;
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    openEditContentModal() {
      if (this.isPublishing) {
        return;
      }

      this.$fullscreenModal.open(
        this,
        CreatorLiteContentEdit,
        { entity: this.entity },
        { closeWithOutclick: false },
      );
    },
    openEditImageModal() {
      if (this.isPublishing) {
        return;
      }

      this.$fullscreenModal.open(
        this,
        CreatorLiteImageSettings,
        { entity: this.entity },
        { closeWithOutclick: false },
      );
    },
    regenerateImage(withNewConcept) {
      if (this.isInProgress || this.isPublishing) {
        return;
      }

      this.entity.status = AI_STATUS.IN_PROGRESS;

      CreatorLiteService.regerenate(this.entity.id, { image: true, visualConcept: withNewConcept })
        .then(resp => {
          this.entity.updateWith(resp);
          this.$toast.clear();
          this.$toast.info(this.tr('We are already working on it! The generation will be completed within 1-2 minutes.'));
          this.initFetchTimer();
          return resp;
        })
        .catch(err => {
          this.error(err);
          this.entity.status = AI_STATUS.DONE;
        });
    },
    regenerateContent() {
      if (this.isInProgress || this.isPublishing) {
        return;
      }

      this.isGeneratingTextContent = true;
      this.entity.status = AI_STATUS.IN_PROGRESS;

      CreatorLiteService.regerenate(this.entity.id, { textContent: true })
        .then(resp => {
          this.entity.updateWith(resp);
          this.$toast.clear();
          this.$toast.info(this.tr('We are already working on it! The generation will be completed within 1-2 minutes.'));
          this.initFetchTimer();
          return resp;
        })
        .catch(err => {
          this.error(err);
          this.entity.status = AI_STATUS.DONE;
          this.isGeneratingTextContent = false;
        });
    },
    publishContent() {
      if (!this.isCompleted || this.isPublishing) {
        return;
      }

      this.$toast.clear();

      if (!this.selectedBrand) {
        this.error(this.tr('Please select a brand to publish the content.'));
        return;
      }

      const sourceProfileIds = this.selectedBrandSourceProfiles
        .filter(sp => this.publishTargetPlatforms.includes(sp.platform))
        .map(sp => sp.id);

      if (sourceProfileIds.length === 0) {
        this.error(this.tr('Please select at least one platform to publish the content.'));
        return;
      }

      this.isPublishing = true;
      this.$toast.info(this.tr('We\'re already on it! Your post will be live in under a minute.'));

      const image = this.$refs.imageRenderer.getImageInBase64();

      CreatorLiteService.publish(this.entity.id, sourceProfileIds, image)
        .then(({ item, publications }) => {
          this.entity.updateWith(item);

          const failedPublications = publications.filter(publication => publication.hasError);
          if (failedPublications.length === 0) {
            this.$toast.success(this.tr('Content successfully published!'));
          }
          else if (failedPublications.length === publications.length) {
            this.error(this.tr('Failed to publish the content.'));
          }
          else {
            this.showToast('warning', this.tr('The content could not be published to the following targets: :targets', {
              ':targets': failedPublications.map(publication => publication.platformLabel).join(', '),
            }));
          }

          return item;
        })
        .catch(err => {
          this.error(err);
          this.loadData();
        })
        .finally(() => this.isPublishing = false);
    },
    initFetchTimer() {
      if (this.fetchTimer !== null) {
        return;
      }

      this.fetchTimer = setInterval(async () => {
        await this.loadData();
        if (!this.isInProgress) {
          this.destroyFetchTimer();
        }
      }, 5000);
    },
    destroyFetchTimer() {
      if (this.fetchTimer !== null) {
        clearTimeout(this.fetchTimer);
        this.fetchTimer = null;
      }
    },
  },
};
</script>
